"use client";

import { useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { money } from "@/lib/money";
import { buildManifest, buildBrandBreakdown, manifestProductName } from "@/lib/manifest";

// Higher % off reads as a brighter green; deeper discounts stand out.
function offGreen(p: number): string {
  if (p >= 85) return "text-emerald-200";
  if (p >= 75) return "text-emerald-300";
  if (p >= 65) return "text-emerald-400";
  if (p >= 55) return "text-emerald-500";
  return "text-emerald-600";
}

// Brand mark from Simple Icons, with a lettered badge fallback for brands that
// have no icon (or if the CDN is unreachable).
function BrandLogo({ name, slug }: { name: string; slug?: string }) {
  const [err, setErr] = useState(false);
  if (slug && !err) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={`https://cdn.simpleicons.org/${slug}/e5e7eb`}
        alt=""
        className="w-5 h-5 object-contain shrink-0"
        onError={() => setErr(true)}
      />
    );
  }
  return (
    <span className="w-5 h-5 rounded bg-white/10 text-[9px] font-bold text-gray-300 flex items-center justify-center shrink-0">
      {name.charAt(0)}
    </span>
  );
}

export function LotManifest({
  listingId,
  title,
  imageUrl,
  msrpCents,
  costCents,
  units,
}: {
  listingId: string;
  title: string;
  imageUrl: string | null;
  msrpCents: number;
  costCents: number;
  units: number;
}) {
  const router = useRouter();
  const [view, setView] = useState<"brand" | "sku">("brand");
  const [query, setQuery] = useState("");

  const skuRows = useMemo(() => buildManifest(listingId, units), [listingId, units]);
  const brandRows = useMemo(
    () => buildBrandBreakdown(listingId, units, msrpCents),
    [listingId, units, msrpCents],
  );
  const pctOff = msrpCents > 0 ? Math.round(((msrpCents - costCents) / msrpCents) * 100) : 0;
  const productName = manifestProductName(title);
  const totalUnits = brandRows.reduce((s, b) => s + b.units, 0) || 1;

  const q = query.trim().toLowerCase();
  const brandVisible = q ? brandRows.filter((b) => b.name.toLowerCase().includes(q)) : brandRows;
  const skuVisible = useMemo(() => {
    const indexed = skuRows.map((row, i) => ({ row, i }));
    if (!q) return indexed;
    return indexed.filter(
      ({ row }) =>
        row.variant.toLowerCase().includes(q) ||
        row.size.toLowerCase().includes(q) ||
        row.dept.toLowerCase().includes(q) ||
        row.upc.includes(q),
    );
  }, [skuRows, q]);

  // Totals for the per-brand footer.
  const tExt = brandRows.reduce((s, b) => s + b.extCostCents, 0);
  const tMsrp = totalUnits ? Math.round(brandRows.reduce((s, b) => s + b.avgMsrpCents * b.units, 0) / totalUnits) : 0;
  const tPrice = totalUnits ? Math.round(tExt / totalUnits) : 0;
  const tOff = tMsrp > 0 ? Math.round(((tMsrp - tPrice) / tMsrp) * 100) : 0;

  const th = "px-4 py-2.5 font-medium text-[10px] uppercase tracking-wider text-gray-500";
  const tab = (active: boolean) =>
    `px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
      active ? "bg-white/10 text-white" : "text-gray-400 hover:text-white"
    }`;

  return (
    <div>
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <div className="flex items-center gap-1 bg-white/[0.03] rounded-xl p-1">
          <button type="button" onClick={() => setView("brand")} className={tab(view === "brand")}>
            Per-Brand Pricing
          </button>
          <button type="button" onClick={() => setView("sku")} className={tab(view === "sku")}>
            Top 100 SKUs
          </button>
        </div>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={view === "brand" ? "Search brand" : "Search variant, size, UPC"}
          className="w-52 bg-white/5 text-sm text-white placeholder:text-gray-600 border border-white/10 rounded-lg px-3 py-1.5 focus:outline-none focus:border-white/25"
        />
      </div>

      <div className="max-h-96 overflow-y-auto border-t border-white/8">
        {view === "brand" ? (
          <table className="w-full text-left text-[13px]">
            <thead className="sticky top-0 bg-[#141414] border-b border-white/8">
              <tr>
                <th className={th}>Brand</th>
                <th className={`${th} text-right`}>Units</th>
                <th className={`${th} text-right`}>% Lot</th>
                <th className={`${th} text-right`}>Avg MSRP</th>
                <th className={`${th} text-right`}>Price/Pair</th>
                <th className={`${th} text-right`}>% Off</th>
                <th className={`${th} text-right`}>Ext Cost</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {brandVisible.map((b) => (
                <tr key={b.name} className="hover:bg-white/[0.03] transition-colors">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2.5">
                      <BrandLogo name={b.name} slug={b.slug} />
                      <span className="font-medium text-gray-200">{b.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-right text-gray-200 tabular-nums">{b.units.toLocaleString()}</td>
                  <td className="px-4 py-2.5 text-right text-gray-400 tabular-nums">
                    {((b.units / totalUnits) * 100).toFixed(1)}%
                  </td>
                  <td className="px-4 py-2.5 text-right text-gray-300 tabular-nums">{money(b.avgMsrpCents, 0)}</td>
                  <td className="px-4 py-2.5 text-right text-gray-200 tabular-nums">{money(b.pricePairCents, 2)}</td>
                  <td className={`px-4 py-2.5 text-right font-semibold tabular-nums ${offGreen(b.pctOff)}`}>
                    {b.pctOff}%
                  </td>
                  <td className="px-4 py-2.5 text-right text-gray-300 tabular-nums">{money(b.extCostCents, 0)}</td>
                </tr>
              ))}
              {brandVisible.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-gray-500">
                    No brands match &ldquo;{query}&rdquo;.
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot className="sticky bottom-0 bg-[#141414] border-t border-white/10">
              <tr className="font-semibold">
                <td className="px-4 py-2.5 text-white">Total</td>
                <td className="px-4 py-2.5 text-right text-white tabular-nums">{totalUnits.toLocaleString()}</td>
                <td className="px-4 py-2.5 text-right text-gray-400 tabular-nums">100%</td>
                <td className="px-4 py-2.5 text-right text-gray-300 tabular-nums">{money(tMsrp, 0)}</td>
                <td className="px-4 py-2.5 text-right text-gray-200 tabular-nums">{money(tPrice, 2)}</td>
                <td className={`px-4 py-2.5 text-right tabular-nums ${offGreen(tOff)}`}>{tOff}%</td>
                <td className="px-4 py-2.5 text-right text-white tabular-nums">{money(tExt, 0)}</td>
              </tr>
            </tfoot>
          </table>
        ) : (
          <table className="w-full text-left text-[13px]">
            <thead className="sticky top-0 bg-[#141414] border-b border-white/8">
              <tr>
                <th className={`${th} w-6`} />
                <th className={`${th} w-[36%]`}>Product Name</th>
                <th className={`${th} w-[10%]`}>Variant</th>
                <th className={th}>Dept</th>
                <th className={th}>Size</th>
                <th className={`${th} text-right`}>MSRP</th>
                <th className={`${th} text-right`}>% Off</th>
                <th className={`${th} text-right`}>PPU</th>
                <th className={`${th} text-right`}>Units</th>
                <th className={`${th} text-right`}>Total</th>
                <th className={th}>Brand IP</th>
                <th className={th}>FBA</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {skuVisible.map(({ row, i }) => (
                <tr
                  key={i}
                  onClick={() => router.push(`/listing/${listingId}/manifest/${i}`)}
                  className="cursor-pointer hover:bg-white/[0.03] transition-colors"
                >
                  <td className="px-4 py-2.5 text-gray-600">&rsaquo;</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-md bg-black/40 overflow-hidden shrink-0 border border-white/5">
                        {imageUrl && (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img src={imageUrl} alt="" className="w-full h-full object-cover" />
                        )}
                      </div>
                      <span className="font-medium text-gray-200 whitespace-nowrap">{productName}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-gray-400">{row.variant}</td>
                  <td className="px-4 py-2.5 text-gray-400 capitalize">{row.dept}</td>
                  <td className="px-4 py-2.5 text-gray-300">{row.size}</td>
                  <td className="px-4 py-2.5 text-right text-gray-300 tabular-nums">{money(msrpCents, 0)}</td>
                  <td className={`px-4 py-2.5 text-right tabular-nums ${offGreen(pctOff)}`}>{pctOff}%</td>
                  <td className="px-4 py-2.5 text-right text-gray-300 tabular-nums">{money(costCents, 2)}</td>
                  <td className="px-4 py-2.5 text-right font-semibold text-white tabular-nums">
                    {row.units.toLocaleString()}
                  </td>
                  <td className="px-4 py-2.5 text-right text-gray-200 tabular-nums">
                    {money(costCents * row.units, 0)}
                  </td>
                  <td className="px-4 py-2.5 text-gray-500">{row.ipRisk}</td>
                  <td className="px-4 py-2.5 text-gray-500">{row.fba ? "Yes" : "No"}</td>
                </tr>
              ))}
              {skuVisible.length === 0 && (
                <tr>
                  <td colSpan={12} className="px-4 py-6 text-center text-gray-500">
                    No SKUs match &ldquo;{query}&rdquo;.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
